import { useEffect, useRef, useState } from "react";
import { HiArrowLeft } from "react-icons/hi";
import { IoIosSearch } from "react-icons/io";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useInfiniteQuery } from "@tanstack/react-query";
import { getLessonUnitDetail } from "@/services/lessonsService";
import { filterParams } from "@/utils/filterParams";
import { Breadcrumb, Input, LoadingSpinner } from "@/components";
import { useDebounce } from "@/hooks/useDebounce";
import { cn } from "@/utils";
import { LessonDto, VISIBILITY } from "@/types";
import { Lesson } from "./LessonDetail";
import { LessonComponent } from "./LessonComponent";

const BLANK_VALUE = "_";
const LIMIT = 20;
const SEARCH_PARAMS = {
  SEARCH: "s",
};

const Units = () => {
  const [openLessonModal, setOpenLessonModal] = useState(false);
  const [lesson, setLesson] = useState<LessonDto | undefined>(undefined);
  const isAtBottomRef = useRef(false);
  const refLesson = useRef<HTMLElement>(null);
  const navigation = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isInitialMount = useRef(true);
  const [searchParams, setSearchParams] = useSearchParams();
  const unitId = Number(id);
  const params = Object.fromEntries([...searchParams]);
  const currentSearch = params[SEARCH_PARAMS.SEARCH] || "";
  const [inputValue, setInputValue] = useState<string>(currentSearch);
  const debouncedSearchTerm = useDebounce(inputValue, 500);

  const {
    data: unitsData,
    isLoading,
    hasNextPage,
    fetchNextPage,
  } = useInfiniteQuery({
    queryKey: ["unit-detail", id, currentSearch],
    queryFn: ({ pageParam = 1 }) => {
      return getLessonUnitDetail(
        unitId,
        filterParams({
          page: pageParam,
          isPagination: 1,
          limit: LIMIT,
          orderBy: "id",
          order: "DESC",
          status: VISIBILITY.public,
          s: currentSearch,
        })
      );
    },
    getNextPageParam: (lastPage: any) => {
      return lastPage.pagination?.hasNextPage
        ? lastPage.pagination.page + 1
        : undefined;
    },
    initialPageParam: 1,
    enabled: unitId > 0,
  });

  const updateSearchParams = (object: { [key: string]: string }) => {
    const inputParams: { [key: string]: string } = params;
    for (const key in object) {
      const value = object[key]?.trim();
      if (value) {
        inputParams[key] = value;
      } else {
        delete inputParams[key];
      }
    }
    setSearchParams(inputParams, { replace: true });
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      updateSearchParams({
        [SEARCH_PARAMS.SEARCH]: inputValue?.trim(),
      });
    }
  };

  useEffect(() => {
    // Skip updating search params on initial mount
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    if (debouncedSearchTerm?.trim()) {
      updateSearchParams({
        [SEARCH_PARAMS.SEARCH]: debouncedSearchTerm?.trim() || "",
      });
    } else {
      updateSearchParams({ [SEARCH_PARAMS.SEARCH]: "" });
    }
  }, [debouncedSearchTerm]);

  useEffect(() => {
    if (!hasNextPage) return;
    window.addEventListener("scroll", checkIfAtBottom);
    return () => {
      window.removeEventListener("scroll", checkIfAtBottom);
    };
  }, [hasNextPage]);

  const handleBack = () => {
    navigation(-1);
  };

  const onOpenLessonModal = (lesson: LessonDto) => {
    setOpenLessonModal(true);
    setLesson(lesson);
  };

  const onCloseLessonModal = () => {
    setOpenLessonModal(false);
    setLesson(undefined);
  };

  const checkIfAtBottom = () => {
    refLesson.current = window.document.getElementById("root");
    if (!refLesson.current) return;
    const { scrollTop, scrollHeight, clientHeight } = refLesson.current;
    const isAtBottom = scrollHeight - scrollTop <= clientHeight + 5; // Ngưỡng 5px

    if (isAtBottom && !isAtBottomRef.current) {
      isAtBottomRef.current = true;
      if (hasNextPage) {
        fetchNextPage();
      }
    } else if (!isAtBottom) {
      isAtBottomRef.current = false;
    }
  };

  const lessonListFlat =
    unitsData?.pages.flatMap((page) => page?.data?.data || []) || [];
  const unitInfo = unitsData?.pages?.[0]?.data?.unit;

  return (
    <section>
      <div className="py-4 space-y-2">
        <Breadcrumb
          breadcrumbs={[
            { label: "Eラーンニング", path: "/e-learning" },
            { label: unitInfo?.title || "-", path: "" },
          ]}
        />
        <button
          className="flex items-center gap-2 text-base font-bold leading-7 hover:text-cyan"
          onClick={handleBack}
        >
          <HiArrowLeft className="w-5 h-5 hover:text-cyan" />{" "}
          <p className="whitespace-pre-line lg:whitespace-nowrap max-w-[800px] xl:max-w-[1000px] truncate">{unitInfo?.title || BLANK_VALUE}</p>
        </button>
      </div>
      <Input
        value={inputValue}
        onChange={(e) => setInputValue(e.target.value)}
        placeholder="学習名で検索"
        icon={<IoIosSearch className="size-4" />}
        className="!h-9 !w-80"
        onKeyDown={handleKeyDown}
        containerClassName="mb-4"
      />

      <div className={cn("space-y-4 text-dark w-full")}>
        {/* lesson  */}
        {isLoading && unitsData === undefined && (
          <LoadingSpinner className="mt-10" />
        )}
        <div
          className={cn(
            "grid grid-cols-4 2xl:grid-cols-5 gap-6 overflow-y-auto"
          )}
        >
          {(lessonListFlat || []).map((lesson) => (
            <LessonComponent
              key={lesson.id}
              lesson={lesson}
              onLessonClick={onOpenLessonModal}
            />
          ))}
        </div>
      </div>

      {/* detail lesson  */}
      <Lesson
        lesson={lesson}
        open={openLessonModal}
        onClose={onCloseLessonModal}
      />
    </section>
  );
};

export default Units;
